import { codeOf, messageOf } from './errors.js';

/**
 * Recovery from running against a different build than the one that served the page.
 *
 * A rolling deploy runs two builds behind one Service for a while, and a tab can stay open
 * across a deploy. Two things break: an asset the page asks for is not on the pod that answers
 * (the server 404s it), and an API call names a procedure the server does not have.
 *
 * A missing asset means the page never rendered, so there is nothing to lose — reload. That
 * happens for the entry bundle in `index.html`'s inline script (nothing in the bundle can
 * recover from the bundle failing to load) and for lazy chunks in `main.tsx`. Both share this
 * key and these limits; the inline script restates them because it runs before any module.
 *
 * An unknown procedure means the page DID render, and may hold an unsaved editor draft, so
 * it is never reloaded from under someone — `components/StaleBuildNotice` offers it.
 */
export const RELOAD_HISTORY_KEY = 'ch-stale-reloads';
export const RELOAD_WINDOW_MS = 60_000;
export const MAX_RELOADS_IN_WINDOW = 3;

/**
 * The reload history with `now` appended, or null when the window is spent. The limit is what
 * stops a build that is genuinely broken — not merely mid-rollout — from reloading forever.
 */
export const nextReloadHistory = (history: readonly number[], now: number): number[] | null => {
    const recent = history.filter((at) => now - at < RELOAD_WINDOW_MS);
    return recent.length >= MAX_RELOADS_IN_WINDOW ? null : [...recent, now];
};

const readHistory = (storage: Storage): number[] => {
    const parsed: unknown = JSON.parse(storage.getItem(RELOAD_HISTORY_KEY) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter((at): at is number => typeof at === 'number') : [];
};

/** Reloads unless the window is spent. Returns whether it did. */
export const reloadForStaleBuild = (): boolean => {
    try {
        const next = nextReloadHistory(readHistory(sessionStorage), Date.now());
        if (!next) return false;
        sessionStorage.setItem(RELOAD_HISTORY_KEY, JSON.stringify(next));
    } catch {
        // Storage refused (private mode, quota) or held garbage: without a guard a reload
        // could loop.
        return false;
    }
    window.location.reload();
    return true;
};

/**
 * tRPC's wording for a path the router does not have (11.x: `No "query"-procedure on path
 * "x"`, and `No procedure found on path "x"` from the batch resolver). Matched on the prefix
 * so a record's own not-found message — also `NOT_FOUND` — never reads as a stale build.
 */
const UNKNOWN_PROCEDURE = /^No (?:"\w+"-procedure|procedure found) on path "/;

/** A tRPC error saying the server has no such procedure: this page is another build. */
export const isStaleBuildError = (error: unknown): boolean =>
    codeOf(error) === 'NOT_FOUND' && UNKNOWN_PROCEDURE.test(messageOf(error) ?? '');
